import { toast } from 'react-toastify';

import { readContract } from '@wagmi/core';

import { ethers } from 'ethers';
import type { Address } from 'viem';

// eslint-disable-next-line deprecate/import
import { wagmiConfig, type WagmiConfigChain } from '@shared/common/providers/LegacyWeb3Provider/wagmi';
import { ERC20__factory } from '@shared/config/contracts/abi-typed';
import { namedWeb3Errors } from '@shared/constants';
import { normalizeError } from '@shared/helpers/displayErrorToast';
import displayWalletErrorToast from '@shared/helpers/displayWalletErrorToast';
import clearSavedConnectionDetails from '@shared/helpers/web3/clearSavedConnectionDetails';
import { numberToWei } from '@shared/helpers/web3/formatters/numberToWei';
import getWalletActionStyles from '@shared/helpers/web3/getWalletActionStyles';
import useWeb3 from '@shared/helpers/web3/hooks/useWeb3';
import useMagicIdUserWalletInfo from '@shared/hooks/useMagicIdUserWalletInfo';

type Props = {
  amount: number | string;
  chainId: WagmiConfigChain['id'];
  spenderAddress: Address;
  tokenAddress: Address;
  tokenDecimals: number;
};

export const useAllowance = () => {
  const { connectWallet } = useWeb3();
  const { getUserWalletType } = useMagicIdUserWalletInfo();

  const checkIsEnoughDepositAllowance = async (props: Props): Promise<boolean | undefined> => {
    const { tokenDecimals, amount, chainId, spenderAddress, tokenAddress } = props;
    const { walletType, userWallet } = getUserWalletType();
    const toastId = Date.now();

    try {
      toast.loading('Checking the deposit allowance...', {
        toastId,
        style: getWalletActionStyles(walletType),
      });

      const data = await readContract(wagmiConfig, {
        abi: ERC20__factory.abi,
        address: tokenAddress,
        functionName: 'allowance',
        chainId,
        args: [userWallet.attributes.address, spenderAddress],
      });

      const allowanceInWei = ethers.BigNumber.from(data || '0');
      const amountInWei = numberToWei(amount, tokenDecimals);

      return allowanceInWei.gte(amountInWei);
    } catch (error) {
      const normalizedError = normalizeError(error);

      const retry = async () => {
        await connectWallet({
          walletType,
          targetAddress: userWallet.attributes.address,
        });

        return checkIsEnoughDepositAllowance(props);
      };

      if (normalizedError.message === namedWeb3Errors.CONNECTOR_NOT_FOUND) {
        clearSavedConnectionDetails();
        return await retry();
      }

      displayWalletErrorToast({
        error,
        unknownExceptionReplaceMessage: namedWeb3Errors.UNKNOWN_TRANSACTION_ERROR,
      });
    } finally {
      toast.dismiss(toastId);
    }
  };

  return { checkIsEnoughDepositAllowance };
};
